import { CommonModule } from "@angular/common";
import { Component, ElementRef, inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormArray, FormGroup, ReactiveFormsModule} from "@angular/forms";
import { MatStepper, MatStepperModule } from "@angular/material/stepper";
import { MobileViewAppointmentComponent } from "../appointment/mobile-view-appointment.component";
import { MobileViewDriverInformationComponent } from "../driver-information/mobile-view-driver-information.component";
import { MobileViewTripLocationComponent } from "../trip-location/mobile-view-trip-location.component";
import { TripRequestFormService } from "../../shared/trip-request-form.service";
import { TripRequestService } from "../../../dashboard/services/trip-request.service";
import { MobileViewPassengerInformationComponent } from "../passenger-information/mobile-view-passenger-information.component";
import { MobileViewTripRequestDocumentsComponent } from "../document-information/mobile-view-trip-request-documents.component";
import { ConsumerProfileService } from "../../../dashboard/profile/services/profile.service";
import { MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { ExpenseType, TRIP_RATE, TripRequestDto, TripRequestType } from "../../../dashboard/models/trip-request-models";
import { MobileViewTripSummaryComponent } from "../trip-summary/mobile-view-trip-summary.component";
import { TripRequestFormFactory } from "../../shared/trip-request-form.factory";
import { TripRequestFormMapper } from "../../shared/trip-request-form.mapper";

@Component({
  selector: 'app-mobile-post-trip-request',
  standalone: true,
  imports: [
    CommonModule,
    MatStepperModule,
    ReactiveFormsModule,
    MobileViewAppointmentComponent,
    MobileViewDriverInformationComponent,
    MobileViewTripLocationComponent,
    MobileViewPassengerInformationComponent,
    MobileViewTripRequestDocumentsComponent,
    MobileViewTripSummaryComponent,
    MatDialogModule,
],
  
  templateUrl: './mobile-view-post-trip-request.component.html',
  styleUrl: './mobile-view-post-trip-request.component.scss',
})

export class MobileViewPostTripRequestComponent implements OnInit, OnDestroy {
    @Input() requestId: number = 0;
    @Input() mode: string = '';
    postTripRequestForm!: FormGroup;
    private tripRequestFormService = inject(TripRequestFormService)
    private tripRequestService = inject(TripRequestService)
    private tripRequestFormFactory = inject(TripRequestFormFactory);
     private tripRequestFormMapper = inject(TripRequestFormMapper);
    private profileService = inject(ConsumerProfileService) 
    private el = inject(ElementRef);
    private dialogRef?: MatDialogRef<any>;
    private dialog = inject(MatDialog);
    private router = inject(Router);
    @ViewChild(MobileViewDriverInformationComponent) driverComponent!: MobileViewDriverInformationComponent;
    @ViewChild(MobileViewTripRequestDocumentsComponent) documentComponent!: MobileViewTripRequestDocumentsComponent;
    @ViewChild('confirmationDialogTemplate', { static: true }) confirmationDialogTemplate!: TemplateRef<any>;
    @ViewChild(MobileViewTripLocationComponent) locationComponent!: MobileViewTripLocationComponent;
    isCofirmationMessage = true;
    dialogTitle = 'Trip Confirmation';
    dialogContent = '';
    confirmationNo = '';
    activeStepperIndex = 0;
    

    get tripRequest() : FormGroup {
      return this.postTripRequestForm.get('tripRequest') as FormGroup;
    }

    get tripAppointments() : FormArray {
      return this.tripRequest.get('tripAppointments') as FormArray;
    }

    get tripAddresses() : FormArray {
      return this.tripRequest.get('tripAddresses') as FormArray;
    }

    get appVerificationDocFg(): FormGroup {
      return this.tripRequest.get('appointmentVerificationDocuments') as FormGroup;
    }

    get tollDocFg(): FormGroup {
      return this.tripRequest.get('tollDocuments') as FormGroup;
    }

    get parkingDocFg(): FormGroup {
      return this.tripRequest.get('parkingDocuments') as FormGroup;
    }

    ngOnInit(): void {
        this.tripRequestFormService.getCountyInfo();
        this.postTripRequestForm = this.tripRequestFormFactory.createPostTripRequestForm();

        if (this.requestId !== 0 && this.mode === 'edit') {
          this.tripRequestService.getPostTripRequest(this.requestId).subscribe((data) => {
            this.tripRequestFormService.tripRate = data.tripRate ?? TRIP_RATE;
            this.tripRequestFormMapper.loadPostTripRequestData(this.tripRequest, data);
            this.locationComponent.setAddressSearchList();
            this.loadDocumentsControls(data);
            this.documentComponent.bindExpenceAmountChange();
            this.documentComponent.setExpenseDocumentRequiredValidator('appointmentVerificationDocuments');
            const tollExpense = data.tripAppointments?.flatMap(app => app.tripExpenses)
                          .find(x => x?.expenseType == ExpenseType.Toll && x?.expenseAmount > 0);
            if (tollExpense) { 
              this.documentComponent.showTollSection = true;
              this.documentComponent.setExpenseDocumentRequiredValidator(ExpenseType.Toll);
            };

            const parkingExpense = data.tripAppointments?.flatMap(app => app.tripExpenses)
                          .find(x => x?.expenseType == ExpenseType.Parking && x?.expenseAmount > 0);
            if (parkingExpense) { 
              this.documentComponent.showParkingSection = true;
              this.documentComponent.setExpenseDocumentRequiredValidator(ExpenseType.Parking);
            };
            if (data.verificationDocuments) {
                this.documentComponent.linkingDocuments = data.verificationDocuments;
                this.documentComponent.dataSource.data = data.verificationDocuments;
            }
            this.tripRequestFormService.loadPostTripLegs(this.tripAppointments,data);
            this.activeStepperIndex = data.stepIndex;
          });
        }
        else if (this.requestId !== 0 && this.mode === 'create') {
          this.tripRequestService.getPreTripRequest(this.requestId).subscribe((data) => {
            data.tripType = TripRequestType.Post;
            this.tripRequestFormMapper.loadPreTripRequestData(this.tripRequest, data)
            this.locationComponent.setAddressSearchList();
            this.tripRequest.get('isAcceptedPreTrip')?.setValue(true);
          })
        }
        else {
          this.tripAddresses.push(this.tripRequestFormFactory.getTripAddressFg(1));
          this.tripAddresses.push(this.tripRequestFormFactory.getTripAddressFg(2));
        }
    }

    goToAppointment(stepper : MatStepper) {
      stepper.selectedIndex = 0
    }
    
    goToDriverInfo(stepper : MatStepper, direction : string) {
      if (direction !== "Back") {
          if (this.tripRequestFormService.validateAppointmentsData(this.tripAppointments)) {
            this.tripRequestFormService.saveAppointments(this.tripRequest, TripRequestType.Post, this.tripRequest.get('isAcceptedPreTrip')?.value).subscribe((data) => {
              if(!data.invalidEligibility) { 
                this.tripRequestFormMapper.loadAppointmentData(this.tripRequest, data)
                stepper.selectedIndex = 1;
              }
              else {
                this.dialogTitle = 'Unverified Eligibility';
                this.dialogContent = 'We are unable to verify your eligibility at this time, please contact your local Medical Assistance transportation office for any questions.'
                this.isCofirmationMessage = false;
                this.openDialog(false);
              }
            })
          }
        }
      else {
        stepper.selectedIndex = 1;
      }
    }

    goToTripLocation(stepper : MatStepper, direction: string) {
      if (direction !== "Back") {
        if (this.tripRequestFormService.validateDriverInformation(this.tripRequest)) {
            this.tripRequestFormService.saveTripDriverInfo(this.tripRequest).subscribe((data) => {
            this.tripRequestFormMapper.loadTripDrivers(this.tripRequest, data)
            this.profileService.resetProfileSavedDrivers();
            this.driverComponent.loadDriverList();
            stepper.selectedIndex = 2;
          })
        }
      }
      else {
        stepper.selectedIndex = 2
      }
    }

    goToPassenger(stepper : MatStepper, direction: string) {
        if (direction !== "Back") {
          if (this.tripRequestFormService.validateTripLocation(this.tripRequest)) {
              this.tripRequestFormService.saveTripLocation(this.tripRequest, TripRequestType.Post).subscribe((data) => {
              if(!data.invalidManualAddress) { 
                this.tripRequestFormMapper.loadAddress(this.tripRequest, data);
                stepper.selectedIndex = 3;
            }
            else {
                  this.dialogTitle = 'Address Error';
                  this.dialogContent = 'The address entered cannot be verified. Please review the entered information and correct the details, if needed.'
                  this.isCofirmationMessage = false;
                  this.openDialog(false);
            }
          });
        }
      }
      else {
        stepper.selectedIndex = 3;
      }
    }

    goToDocuments(stepper : MatStepper, direction: string) {
      if (direction !== "Back") {
        if (this.tripRequestFormService.validatePassenger(this.tripRequest)) {
              this.tripRequestFormService.savePassengers(this.tripRequest).subscribe((data) => {
              this.tripRequestFormMapper.loadPassenger(this.tripRequest, data)
              if (data.verificationDocuments) {
                this.documentComponent.linkingDocuments = data.verificationDocuments;
                this.documentComponent.dataSource.data = data.verificationDocuments;
              }
              this.documentComponent.bindExpenceAmountChange();
              stepper.selectedIndex = 4;
          })
        }
      }
      else {
        stepper.selectedIndex = 4;
      }
    }

    goToSummary(stepper : MatStepper, direction: string) {
      if (direction !== "Back") {
        if (this.validateDoument()) {
            const formData = this.getDocumentFormData();
            this.tripRequestService.saveTripDocuments(formData).subscribe((data) => {
            this.tripRequestFormMapper.loadTripDocuments(this.tripRequest, data);
            this.loadDocumentsControls(data);
            this.tripRequestFormService.tripRate = data.tripRate ?? TRIP_RATE;
            this.tripRequestFormService.loadPostTripLegs(this.tripAppointments,data);
            stepper.selectedIndex = 5;
          });         
        }
      }
      else {
          stepper.selectedIndex = 5;
      }
    }
    
    loadDocumentsControls(data: TripRequestDto) {
      this.documentComponent.appVerficationDocumentsDto = [];
      this.documentComponent.tollDocumentsDto = [];
      this.documentComponent.parkingDocumentsDto = [];

      const verificationDocs = data.tripDocuments?.filter(d => d.documentType === 'Verification');
      if (verificationDocs) {
        this.documentComponent.appVerficationDocumentsDto = verificationDocs;
        this.appVerificationDocFg.patchValue({
            appointmentDocument:  verificationDocs.length > 0 ? `${verificationDocs.length} file(s) selected` : null 
        })
      }

      const tollDocs = data.tripDocuments?.filter(d => d.documentType === ExpenseType.Toll);
      if (tollDocs) {
        this.documentComponent.tollDocumentsDto = tollDocs;
        this.tollDocFg.patchValue({
            tollDocument:  tollDocs.length > 0 ? `${tollDocs.length} file(s) selected` : null
        })
      }

      const parkingDocs = data.tripDocuments?.filter(d => d.documentType === ExpenseType.Parking);
      if (parkingDocs) {
        this.documentComponent.parkingDocumentsDto = parkingDocs;
        this.parkingDocFg.patchValue({
            parkingDocument: parkingDocs.length > 0 ? `${parkingDocs.length} file(s) selected` : null
        })
      }
    }

    saveTripSumary() {
      if (this.tripRequestFormService.validateTripSummary(this.tripRequest, TripRequestType.Post)) {
        this.tripRequestFormService.saveTripSummary(this.tripRequest, TripRequestType.Post).subscribe(data => {
          this.confirmationNo = data;
          this.dialogTitle = 'Request Confirmation';
          this.dialogContent = 'The trip has been successfully submitted. Your confirmation number is ' + this.confirmationNo;
          this.isCofirmationMessage = true
          this.openDialog(true);
        })
      }

    }

    getDocumentFormData(): FormData {

      const documetFormData = this.tripRequestFormMapper.getDocumentFormData(this.tripRequest);

      let documentIndex = 0;
      this.documentComponent.appVerficationDocumentsDto.forEach((document) => {
        documetFormData.append(`tripDocuments[${documentIndex}].documentId`, document.documentId.toString());
        documetFormData.append(`tripDocuments[${documentIndex}].fileName`, document.fileName);
        documetFormData.append(`tripDocuments[${documentIndex}].path`, document.path);
        documetFormData.append(`tripDocuments[${documentIndex}].documentType`, 'Verification');
        if (document.file) {
          documetFormData.append(`tripDocuments[${documentIndex}].file`, document.file);
        }

        if (document.linkDocumentId) {
          documetFormData.append(`tripDocuments[${documentIndex}].linkDocumentId`, document.linkDocumentId.toString());
        }
        documentIndex++;
      })

      this.documentComponent.tollDocumentsDto.forEach((document) => {
        documetFormData.append(`tripDocuments[${documentIndex}].documentId`, document.documentId.toString());
        documetFormData.append(`tripDocuments[${documentIndex}].fileName`, document.fileName);
        documetFormData.append(`tripDocuments[${documentIndex}].path`, document.path);
        documetFormData.append(`tripDocuments[${documentIndex}].documentType`, ExpenseType.Toll);
        if (document.file) {
          documetFormData.append(`tripDocuments[${documentIndex}].file`, document.file);
        }
        documentIndex++;
      })

      this.documentComponent.parkingDocumentsDto.forEach((document) => {
        documetFormData.append(`tripDocuments[${documentIndex}].documentId`, document.documentId.toString());
        documetFormData.append(`tripDocuments[${documentIndex}].fileName`, document.fileName);
        documetFormData.append(`tripDocuments[${documentIndex}].path`, document.path);
        documetFormData.append(`tripDocuments[${documentIndex}].documentType`, ExpenseType.Parking);
        if (document.file) {
          documetFormData.append(`tripDocuments[${documentIndex}].file`, document.file);
        }
        documentIndex++;
      })
      return documetFormData;
    }

    validateDoument(): boolean {
      let validation = true;
      if (this.documentComponent.isAppVerificationDocReq 
          && (this.appVerificationDocFg.get('appointmentDocument')?.invalid || !this.appVerificationDocFg.get('appointmentDocument')?.value)) {
            this.appVerificationDocFg.get('appointmentDocument')?.markAsTouched();
            validation = false;
      }

      if (this.documentComponent.isTollDocReq 
          && (this.tollDocFg.get('tollDocument')?.invalid || !this.tollDocFg.get('tollDocument')?.value )) {
        this.tollDocFg.get('tollDocument')?.markAsTouched();
        validation = false;
      }

      if (this.documentComponent.isParkingDocReq 
        && (this.parkingDocFg.get('parkingDocument')?.invalid || !this.parkingDocFg.get('parkingDocument')?.value)) {
          this.parkingDocFg.get('parkingDocument')?.markAsTouched();
          validation = false;
      }

      return validation;
    }

    openDialog(disableClose: boolean) {
      const rect = this.el.nativeElement.getBoundingClientRect();
      const config = new MatDialogConfig();
      config.width = '80%';
      config.disableClose= disableClose;
      this.dialogRef = this.dialog.open(this.confirmationDialogTemplate, config);
    }

    closeDialog() {
      if (this.isCofirmationMessage) { this.router.navigate(['/dashboard']); }
      this.dialogRef?.close();
    }

    ngOnDestroy(): void {
      this.tripRequestFormService.consumerEnteredMilesSub.forEach(sub => sub.unsubscribe());
    }
}
